import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

/**
 * TenantRootRouter - Smart router for tenant subdomain root path
 * 
 * Routes based on portal_mode and storefront_enabled settings:
 * 
 * 1. Unified + Storefront Enabled → /customers (storefront)
 * 2. Unified + Storefront Disabled → /customers/login (customer portal login)
 * 3. Separate + Storefront Enabled → Block page (must use /customers/{storeslug})
 * 4. Separate + Storefront Disabled → Block page (must use /customers/{storeslug}/login)
 */
const TenantRootRouter = ({ tenantSubdomain }) => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // v4.15 — the tenant identity MUST ride along: /api/settings/public
        // resolves the tenant only from a token or an explicit param (it
        // never reads the Host header). Without ?subdomain= an anonymous
        // first visit got the DEFAULTS (storefront_enabled=false) and the
        // root always bounced to /customers/login even with the storefront
        // on — the production root-redirect bug.
        const params = new URLSearchParams();
        if (tenantSubdomain) params.set('subdomain', tenantSubdomain);
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/settings/public?${params.toString()}`
        );
        if (response.ok) {
          const data = await response.json();
          setSettings(data);
        } else {
          setError('Failed to load settings');
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
        setError('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [tenantSubdomain]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    // Fallback to login on error
    return <Navigate to="/login" replace />;
  }

  const portalMode = settings?.customer_portal_mode || 'unified';
  const storefrontEnabled = settings?.storefront_enabled || false;

  // Case 1: Unified Portal + Storefront Enabled → Redirect to /customers
  if (portalMode === 'unified' && storefrontEnabled) {
    return <Navigate to="/customers" replace />;
  }

  // Case 2: Unified Portal + Storefront Disabled → Redirect to /customers/login
  if (portalMode === 'unified' && !storefrontEnabled) {
    return <Navigate to="/customers/login" replace />;
  }

  // Case 3 & 4: Separate Portal Mode → Show block page
  if (portalMode === 'per_store') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          
          <h1 className="text-2xl font-bold text-slate-900 mb-3">
            Store-Specific Access Required
          </h1>
          
          <p className="text-slate-600 mb-6 leading-relaxed">
            This tenant uses store-specific portals. Please access your store using the correct URL:
          </p>
          
          <div className="bg-slate-50 rounded-lg p-4 mb-6 border border-slate-200">
            {storefrontEnabled ? (
              <>
                <p className="text-sm font-semibold text-slate-700 mb-2">For Shopping:</p>
                <code className="text-sm text-teal-600 font-mono bg-white px-3 py-2 rounded border border-teal-200 block">
                  {tenantSubdomain}.plateos.io/customers/[store-name]
                </code>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-slate-700 mb-2">For Portal Access:</p>
                <code className="text-sm text-teal-600 font-mono bg-white px-3 py-2 rounded border border-teal-200 block">
                  {tenantSubdomain}.plateos.io/customers/[store-name]/login
                </code>
              </>
            )}
          </div>
          
          <p className="text-xs text-slate-500">
            Replace <code className="bg-slate-100 px-1 rounded">[store-name]</code> with your specific store identifier.
          </p>
          
          {storefrontEnabled && (
            <p className="text-xs text-slate-400 mt-4">
              Don&apos;t know your store URL? Contact your store administrator.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Fallback (should not reach here)
  return <Navigate to="/login" replace />;
};

export default TenantRootRouter;
